'use client';

import { useEffect, useState } from 'react';
import { type Outage } from '@/lib/outages-db';
import { OutageTimeline } from '@/components/outages/OutageTimeline';
import { OutageFilters, type OutageFilterState } from '@/components/outages/OutageFilters';
import { useTheme } from '@/components/ThemeProvider';
import { trackToolUsage } from '@/components/RecentlyUsedTools';

export default function OutagesPage() {
  const { theme } = useTheme();
  const [outages, setOutages] = useState<Outage[]>([]);
  const [vendors, setVendors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<OutageFilterState>({
    vendor: '',
    severity: '',
    status: '',
    startDate: '',
    endDate: '',
    impact_scope: '',
  });

  // Fetch outages
  useEffect(() => {
    const fetchOutages = async () => {
      setLoading(true);
      setError(null);

      try {
        // Build query string
        const params = new URLSearchParams();
        if (filters.vendor) params.append('vendor', filters.vendor);
        if (filters.severity) params.append('severity', filters.severity);
        if (filters.status) params.append('status', filters.status);
        if (filters.startDate) params.append('startDate', new Date(filters.startDate).toISOString());
        if (filters.endDate) params.append('endDate', new Date(filters.endDate).toISOString());
        if (filters.impact_scope) params.append('impact_scope', filters.impact_scope);
        params.append('limit', '100'); // Get all outages

        const response = await fetch(`/api/public/outages?${params.toString()}`);

        if (!response.ok) {
          throw new Error('Failed to fetch outages');
        }

        const data = await response.json();
        setOutages(data.data || []);

        // Extract unique vendors from all outages for the filter dropdown
        if (filters.vendor === '' && data.data) {
          const uniqueVendors = Array.from(new Set(data.data.map((o: Outage) => o.vendor))).sort();
          setVendors(uniqueVendors as string[]);
        }
      } catch (err) {
        console.error('Error fetching outages:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchOutages();
  }, [filters]);

  // Track tool usage for "Recently Used" section
  useEffect(() => {
    trackToolUsage('/outages');
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme === 'dark' ? '#0A1E2E' : 'white' }}>
      {/* Header */}
      <div
        className="border-b border-gray-200 dark:border-border-muted backdrop-blur-sm sticky top-0 z-20"
        style={{
          backgroundColor: theme === 'dark' ? 'rgba(10, 30, 46, 0.5)' : '#f9fafb',
          borderBottomColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb'
        }}
      >
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl">
            <h1
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: theme === 'dark' ? '#F5EBD7' : '#111827' }}
            >
              Major Outage Tracker
            </h1>
            <p
              className="text-lg"
              style={{ color: theme === 'dark' ? 'rgba(245, 235, 215, 0.8)' : '#374151' }}
            >
              A comprehensive timeline of major industry-affecting outages from 2020 to present.
              Learn from past incidents, understand patterns, and build more resilient systems.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Filters */}
          <OutageFilters filters={filters} onFilterChange={setFilters} vendors={vendors} />

          {/* Error State */}
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-400 rounded-lg p-6 mb-8">
              <h3 className="font-semibold mb-2">Error loading outages</h3>
              <p>{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* Stats Summary */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white dark:bg-background/50 border border-gray-200 dark:border-border-muted rounded-lg p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-cream/10 rounded w-24 mb-3"></div>
                  <div className="h-9 bg-gray-200 dark:bg-cream/10 rounded w-16"></div>
                </div>
              ))}
            </div>
          ) : !error && outages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div
                className="rounded-lg p-6"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(10, 30, 46, 0.5)' : 'white',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb'
                }}
              >
                <div style={{ color: theme === 'dark' ? 'rgba(245, 235, 215, 0.7)' : '#374151' }} className="text-sm mb-1">Total Outages</div>
                <div style={{ color: theme === 'dark' ? '#F5EBD7' : '#111827' }} className="text-3xl font-bold">{outages.length.toLocaleString()}</div>
              </div>
              <div
                className="rounded-lg p-6"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(10, 30, 46, 0.5)' : 'white',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: theme === 'dark' ? 'rgba(239, 68, 68, 0.2)' : '#fecaca'
                }}
              >
                <div style={{ color: theme === 'dark' ? 'rgba(248, 113, 113, 0.7)' : '#dc2626' }} className="text-sm mb-1">Critical</div>
                <div style={{ color: theme === 'dark' ? '#f87171' : '#dc2626' }} className="text-3xl font-bold">
                  {outages.filter((o) => o.severity === 'critical').length.toLocaleString()}
                </div>
              </div>
              <div
                className="rounded-lg p-6"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(10, 30, 46, 0.5)' : 'white',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.2)' : 'rgba(232, 152, 65, 0.3)'
                }}
              >
                <div style={{ color: theme === 'dark' ? 'rgba(232, 152, 65, 0.7)' : '#c2410c' }} className="text-sm mb-1">Major</div>
                <div style={{ color: theme === 'dark' ? '#E89841' : '#c2410c' }} className="text-3xl font-bold">
                  {outages.filter((o) => o.severity === 'major').length.toLocaleString()}
                </div>
              </div>
              <div
                className="rounded-lg p-6"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(10, 30, 46, 0.5)' : 'white',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: theme === 'dark' ? 'rgba(234, 179, 8, 0.2)' : '#fde047'
                }}
              >
                <div style={{ color: theme === 'dark' ? 'rgba(250, 204, 21, 0.7)' : '#a16207' }} className="text-sm mb-1">Moderate</div>
                <div style={{ color: theme === 'dark' ? '#facc15' : '#a16207' }} className="text-3xl font-bold">
                  {outages.filter((o) => o.severity === 'moderate').length.toLocaleString()}
                </div>
              </div>
            </div>
          ) : null}

          {/* Timeline */}
          <OutageTimeline outages={outages} loading={loading} />
        </div>
      </div>
    </div>
  );
}
